/*******************************
 * This component uses jotai async derived atoms, 
 * so it requires suspense wrapper around it when used
/*******************************/

import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import { Collapse } from 'reactstrap';

import {
  historyChartDataAtom,
  historyChartLabelsAtom,
  intervalAtom,
  newDataGenerationIndexAtom,
  appliedWAIdsAtom,
  severityFiltersAtom,
  showHistoryChartCountsAtom,
  LastSyncDateAtom,
  waCurrentVulnCountAtom,
} from './state';
import HistoryChart from '../components/HistoryChart';
import {
  getSeverityFilterValues,
  getVulnStatusSum,
  getLastIntervalDate,
  getHistoryDrillDownUrl,
  getChartSubtitle,
  getLastIntervalCount,
} from '../helper';
import { IntervalFilter, TooltipLabelsType, VulnStatus } from '../types';

import '../styles.css';

type Props = {
  tooltipLabels: TooltipLabelsType;
  setModalComponentData: (ModalDialogData) => void;
};

const HistoryCharts = ({ tooltipLabels }: Props) => {
  const showHistoryChartCounts = useAtomValue(showHistoryChartCountsAtom);
  const LastSyncDate = useAtomValue(LastSyncDateAtom);
  const historyChartData = useAtomValue(historyChartDataAtom);
  const historyChartLabels = useAtomValue(historyChartLabelsAtom);
  const newDataGenerationIndex = useAtomValue(newDataGenerationIndexAtom);
  const interval = useAtomValue(intervalAtom);
  const severityFilters = useAtomValue(severityFiltersAtom);
  const selectedWAIds = useAtomValue(appliedWAIdsAtom);
  const waCurrentVulnCount = useAtomValue(waCurrentVulnCountAtom);

  const [expandedChartKey, setExpandedChartKey] = useState<string | undefined>(
    undefined,
  );
  const [expandedChartOpen, setExpandedChartOpen] = useState(false);

  function getHistoryChartsDrillDownUrl(type: string) {
    if (!type) type = 'all';
    let commonParameters = {
      type: type,
      severity: '',
      sourceType: 'was_data',
      issueType: 'Vulnerability',
      qid: '',
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedWAIds,
    };

    let lastIntervalDate = getLastIntervalDate(interval);

    const historyChartDrillDownUrl = getHistoryDrillDownUrl(
      commonParameters,
      type,
      lastIntervalDate,
    );

    return historyChartDrillDownUrl;
  }

  // This function returns the date since we are counting the number of vulnerabilities
  function getChartTitleDate(prefix: string) {
    if (historyChartLabels.length === 1) {
      return `${prefix} (All)`;
    }
    let lastCompleteDateLabel = historyChartLabels[0];
    if (historyChartLabels.length > 2) {
      lastCompleteDateLabel = historyChartLabels[historyChartLabels.length - 2];
    }
    let currentYear = new Date().getFullYear();
    let fullDateString = lastCompleteDateLabel + ' ' + currentYear;
    let lastCompleteDate = new Date(fullDateString);
    lastCompleteDate.setDate(lastCompleteDate.getDate() + 1);
    return `${prefix} (Since ${lastCompleteDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })})`;
  }

  function getChartTitle(prefix: string) {
    switch (interval) {
      case IntervalFilter.Week:
      case IntervalFilter.FourWeeks:
      case IntervalFilter.ThreeMonth:
      case IntervalFilter.SixMonth:
        return getChartTitleDate(prefix);
      default:
        return prefix;
    }
  }

  const chartPropertiesDictionary = {
    new: {
      title: getChartTitle('New'),
      subtitle: getChartSubtitle('New', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.New,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.New]
        : [],
      onExpandClick: () => setExpandedChartComponent('new'),
      // colors: ["rgb(255,105,0)", chroma("rgb(255,105,0)").darken(1.5).css()],
      colors: ['#0096FF'],
      showCounts: showHistoryChartCounts,
      selectedInterval: interval,
      lastIntervalDrillDownUrl: getHistoryChartsDrillDownUrl('lastIntervalNew'),
      getDrillDownUrl: () => getHistoryChartsDrillDownUrl('new'),
      newDataGenerationIndex,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.New,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.New] : [])
    },
    reopened: {
      title: getChartTitle('Reopened'),
      subtitle: getChartSubtitle('Reopened', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.Reopened,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.Reopened]
        : [],
      onExpandClick: () => setExpandedChartComponent('reopened'),
      // colors: ["rgb(254,45,45)", chroma("rgb(254,45,45)").darken(1.5).css()],
      colors: ['#0096FF'],
      showCounts: showHistoryChartCounts,
      selectedInterval: interval,
      lastIntervalDrillDownUrl: getHistoryChartsDrillDownUrl(
        'lastIntervalReopened',
      ),
      getDrillDownUrl: () => getHistoryChartsDrillDownUrl('reopened'),
      newDataGenerationIndex,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.Reopened,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.New] : [])
    },
    fixed: {
      title: getChartTitle('Fixed'),
      subtitle: getChartSubtitle('Fixed', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.Fixed,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.Fixed]
        : [],
      onExpandClick: () => setExpandedChartComponent('fixed'),
      // colors: ["rgb(113,206,126)", chroma("rgb(113,206,126)").darken(1.5).css()],
      colors: ['#0096FF'],
      showCounts: showHistoryChartCounts,
      selectedInterval: interval,
      lastIntervalDrillDownUrl:
        getHistoryChartsDrillDownUrl('lastIntervalFixed'),
      getDrillDownUrl: () => getHistoryChartsDrillDownUrl('fixed'),
      newDataGenerationIndex,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.Fixed,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.New] : [])
    },
    ignored: {
      title: getChartTitle('Ignored'),
      subtitle: getChartSubtitle('Ignored', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.Ignored,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.Ignored]
        : [],
      onExpandClick: () => setExpandedChartComponent('ignored'),
      // colors: ["rgb(252,185,0)", chroma("rgb(252,185,0)").darken(1.5).css()],
      colors: ['#0096FF'],
      showCounts: showHistoryChartCounts,
      selectedInterval: interval,
      lastIntervalDrillDownUrl: getHistoryChartsDrillDownUrl(
        'lastIntervalIgnored',
      ),
      getDrillDownUrl: () => getHistoryChartsDrillDownUrl('ignored'),
      newDataGenerationIndex,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.Ignored,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.Ignored] : [])
    },
  };

  const setExpandedChartComponent = (propertiesKey: string) => {
    // console.log("setExpandedChartComponent", propertiesKey);
    if (expandedChartKey === propertiesKey) {
      setExpandedChartOpen(false);
      setTimeout(() => {
        setExpandedChartKey(undefined);
      }, 1000);
    } else if (expandedChartKey !== undefined) {
      setExpandedChartOpen(false);
      setTimeout(() => {
        setExpandedChartKey(undefined);
      }, 500);
      setTimeout(() => {
        setExpandedChartOpen(true);
        setExpandedChartKey(propertiesKey);
      }, 1000);
    } else {
      setExpandedChartOpen(true);
      setExpandedChartKey(propertiesKey);
    }
  };

  return (
    <>
      {historyChartData && (
        <div className="row">
          <div className="col-12">
            <HistoryChart
              title="All"
              labels={historyChartLabels}
              tooltipLabels={tooltipLabels}
              datasets={
                historyChartData.historyChartSeverityData[VulnStatus.All]
              }
              // colors={[chroma("#055193").brighten(1.5).css(), "#055193"]}
              colors={['#0096FF']}
              showCounts={showHistoryChartCounts}
              getDrillDownUrl={() => getHistoryChartsDrillDownUrl('open')}
              newDataGenerationIndex={newDataGenerationIndex}
              lastSyncedDate={LastSyncDate}
              currentVulnCount={waCurrentVulnCount}
            ></HistoryChart>
          </div>

          <Collapse isOpen={expandedChartOpen} className="col-12">
            {expandedChartKey && (
              <HistoryChart
                key="expandedChart"
                {...chartPropertiesDictionary[expandedChartKey]}
                isActive={true}
              />
            )}
          </Collapse>
          <div className="col-md-3">
            <HistoryChart
              {...chartPropertiesDictionary['new']}
              isActive={expandedChartKey === 'new'}
            />
          </div>
          <div className="col-md-3">
            <HistoryChart
              {...chartPropertiesDictionary['reopened']}
              isActive={expandedChartKey === 'reopened'}
            />
          </div>
          <div className="col-md-3">
            <HistoryChart
              {...chartPropertiesDictionary['fixed']}
              isActive={expandedChartKey === 'fixed'}
            />
          </div>
          <div className="col-md-3">
            <HistoryChart
              {...chartPropertiesDictionary['ignored']}
              isActive={expandedChartKey === 'ignored'}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HistoryCharts;
